import axios from 'axios';
import ThemedSelect from 'components/commonbreadcrumb/ThemedSelect';
import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const MedicationInstructionsModal = ({ show, handleClose, dataObj, splitObj, onSaved }) => {
  const isAddMode = !splitObj?.length;
  const { id } = useParams();
  const [finalData, setData] = useState([]);
  const [frequency] = useState(dataObj?.frequency || []);
  const [amount] = useState(dataObj?.amount || []);
  const [hours] = useState(dataObj?.prescription_hour || []);
  const [activeRow, setActiveRow] = useState(null);
  const [bottomIdx, setBottomIdx] = useState(null);

  const blankRow = () => ({
    sig: '',
    amount: '',
    dose: '',
    selectedFreq: null,
    hours: ['', '', '', ''],
    med_type: ''            // '' | 'P' | 'O'
  });

  useEffect(() => {
    if (!show) return;

    if (isAddMode) {
      setData([blankRow(), blankRow()]);
      setActiveRow(null);
      setBottomIdx(1);
    } else {
      const firstTwo = splitObj.slice(0, 2);
      const prnIdx = firstTwo.findIndex(p => String(p.med_type || '').toUpperCase() === 'P');

      const initial = firstTwo.map((p, idx) => {
        const freqObj =
          dataObj?.frequency.find(f => Number(f.id) === Number(p.frequency_id)) || null;
        return {
          sr: idx + 1,
          id: p.id,
          sr_id: p.sr_id,
          sig: p.sig,
          amount: p.amount_id ?? '',
          dose: p.dose ?? '',
          selectedFreq: freqObj,
          hours: [p.time_1 ?? '', p.time_2 ?? '', p.time_3 ?? '', p.time_4 ?? ''],
          med_type: p.med_type ?? ''
        };
      });

      setData(initial);
      setActiveRow(prnIdx >= 0 ? prnIdx : null);
      setBottomIdx(prnIdx >= 0 ? prnIdx : Math.min(firstTwo.length - 1, 1));
    }
  }, [show, isAddMode, splitObj, dataObj]);

  const handleInputChange = (index, field, value) => {
    setData((prev) => {
      const next = [...prev];
      next[index][field] = value;
      return next;
    });
  };

  const handleHourChange = (index, hourIndex, value) => {
    setData((prev) => {
      const next = [...prev];
      if (!next[index].hours) next[index].hours = [];
      next[index].hours[hourIndex] = value;
      return next;
    });
  };

  const rowsForRender = useMemo(() => {
    const arr = finalData.map((item, idx) => ({ item, idx }));
    if (bottomIdx != null && bottomIdx >= 0 && bottomIdx < arr.length) {
      const stick = arr.splice(bottomIdx, 1)[0];
      arr.push(stick);
    }
    return arr;
  }, [finalData, bottomIdx]);


  const handleTogglePrn = (idx, checked) => {
    setData(prev =>
      prev.map((row, i) => {
        if (checked) {
          if (i === idx) return { ...row, med_type: 'P' };
          if (row.med_type === 'P') return { ...row, med_type: 'O' };
          return row;
        } else {
          if (i === idx) return { ...row, med_type: 'O' };
          return row;
        }
      })
    );
    setActiveRow(checked ? idx : null);
    if (checked) setBottomIdx(idx);
  };

  const handleSave = async () => {
    for (let i = 0; i < finalData.length; i++) {
      const item = finalData[i];
      const isPRN =
        String(item.med_type || '').toUpperCase() === 'P' || activeRow === i;

      if (!item.amount) {
        toast.error(`Amount is required for row ${i + 1}`);
        return;
      }

      const doseNum = item.dose;
      if (!doseNum) {
        toast.error(`dose is required for row ${i + 1}`);
        return;
      }

      if (!item.selectedFreq) {
        toast.error(`Frequency is required for row ${i + 1}`);
        return;
      }

      // Only require hours for non-PRN rows
      if (!isPRN) {
        if (!item.hours || item.hours.every(h => !h)) {
          toast.error(`At least one hour must be selected for row ${i + 1}`);
          return;
        }
      }
    }


    const payload = finalData.map((item, idx) => {
      const base = {
        dose: item.dose,
        amount_id: Number(item.amount),
        frequency_id: Number(item.selectedFreq.id),
        time_1: item.hours?.[0] || null,
        time_2: item.hours?.[1] || null,
        time_3: item.hours?.[2] || null,
        time_4: item.hours?.[3] || null,
        prescription_id: Number(id),
        sr_id: isAddMode ? idx + 1 : item.sr_id,
        med_type: item.med_type || null,
        sig: item.sig || ''
      };
      if (item.id) base.id = item.id;
      return base;
    });

    try {
      const tokenStr = localStorage.getItem('accessToken');
      const { data } = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/prescriptions/update-splitmends`,
        payload,
        { headers: { Authorization: `Bearer ${tokenStr}` } }
      );

      if (data.status) {
        const serverRows = Array.isArray(data.splitmeds) ? data.splitmeds : null;
        const normalized = (serverRows ?? finalData).map((row, idx) => ({
          id: serverRows ? row.id : (row.id ?? undefined),
          sr_id: isAddMode ? idx + 1 : (serverRows ? row.sr_id : row.sr_id),
          sig: serverRows ? (row.sig ?? '') : (row.sig ?? ''),
          amount_id: serverRows ? row.amount_id : row.amount,
          dose: serverRows ? row.dose : Number(row.dose),
          frequency_id: serverRows ? row.frequency_id : row.selectedFreq.id,
          time_1: serverRows ? row.time_1 : (row.hours?.[0] || null),
          time_2: serverRows ? row.time_2 : (row.hours?.[1] || null),
          time_3: serverRows ? row.time_3 : (row.hours?.[2] || null),
          time_4: serverRows ? row.time_4 : (row.hours?.[3] || null),
          med_type: serverRows ? row.med_type : (row.med_type || '')
        }));

        onSaved?.(normalized);
        setData(finalData);
        toast.success(isAddMode ? 'Splitmeds added successfully' : 'Splitmeds updated successfully');
        handleClose();
      } else {
        toast.error('Something went wrong');
      }
    } catch {
      toast.error('Request failed');
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton className="border-bottom-0">
        <Modal.Title className="fw-semibold">
          Splitmeds
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {finalData.length > 0 ? (
          <div className="d-flex flex-column gap-3">
            {rowsForRender.map(({ item, idx }, renderIdx) => {
              const isBottom = renderIdx === rowsForRender.length - 1;
              const isActive = activeRow === idx;
              const hoursDisabled = isActive;

              return (
                <div
                  key={idx}
                  className={`card shadow-sm border-0 ${isActive ? 'ring-2' : ''}`}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="card-header bg-white d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      {isActive && <span className="badge bg-success">PRN</span>}
                    </div>
                    {isBottom && (
                      <Form.Check
                        type="switch"
                        id={`prn-row-${idx}`}
                        label="PRN"
                        checked={isActive}
                        onChange={(e) => handleTogglePrn(idx, e.target.checked)}
                        className="m-0"
                      />
                    )}
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <Form.Label className="fw-semibold">Direction</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={5}
                          placeholder="Enter directions"
                          value={item.sig || ''}
                          onChange={(e) => handleInputChange(idx, 'sig', e.target.value)}
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="row g-3">
                          <div className="col-6 col-md-3">
                            <Form.Label className="col-12">Amount</Form.Label>
                            <Form.Select
                              value={item.amount || ''}
                              onChange={(e) => handleInputChange(idx, 'amount', e.target.value)}
                              size="sm"
                            >
                              <option value="">Select</option>
                              {amount.map((opt) => (
                                <option key={opt.id} value={opt.id}>
                                  {opt.amount}
                                </option>
                              ))}
                            </Form.Select>
                          </div>

                          <div className="col-6 col-md-3">
                            <Form.Label className="col-12">Dose</Form.Label>
                            <Form.Control
                              type="text"
                              // min="0"
                              // step="0.01"
                              size="sm"
                              placeholder="Dose"
                              value={item.dose || ''}
                              onChange={(e) => handleInputChange(idx, 'dose', e.target.value)}
                            />
                          </div>

                          <div className="col-12 col-md-6">
                            <Form.Label className="col-12">Frequency</Form.Label>
                            <ThemedSelect
                              name="freq"
                              closeMenuOnSelect
                              placeholder="Select frequency"
                              classNamePrefix="react-select"
                              options={frequency}
                              value={item.selectedFreq}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              onChange={(selected) => {
                                const updated = [...finalData];
                                updated[idx].selectedFreq = selected || null;
                                setData(updated);
                              }}
                            />
                          </div>
                        </div>
                        <div className="mt-4">
                          <div className="small text-muted fw-semibold mb-1">
                            Hours {hoursDisabled && <span className="text-muted">(not required for PRN)</span>}
                          </div>
                          <div className="row g-2 opacity-100" style={hoursDisabled ? { opacity: 0.6 } : undefined}>
                            {[0, 1, 2, 3].map((hourIndex) => (
                              <div className="col-6 col-md-3" key={hourIndex}>
                                <Form.Select
                                  value={item.hours?.[hourIndex] || ''}
                                  onChange={(e) => handleHourChange(idx, hourIndex, e.target.value)}
                                  size="sm"
                                  disabled={hoursDisabled}
                                >
                                  <option value="">--</option>
                                  {hours.map((opt) => (
                                    <option key={opt.id} value={opt.id}>{opt.time}</option>
                                  ))}
                                </Form.Select>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center text-muted">No medication data found.</div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="success" onClick={handleSave}>
          Save
        </Button>
        <Button
          variant="outline-secondary"
          onClick={() => {
            setData([]);
            handleClose();
          }}
        >
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MedicationInstructionsModal;
